'use client';

import { CheckCircle2 } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { SITE } from '@/app/constants';
import { isValidProgramWorkUrl } from './utils';

export type ProgramWorkSubmitText = {
  cancel: string;
  confirm: string;
  submitted: string;
  submitting: string;
  uploadFailed: string;
  uploadTitle: string;
  workLink: string;
  workUrlInvalid: string;
  workUrlPlaceholder: string;
  title?: string;
  titlePlaceholder?: string;
  description?: string;
  descriptionPlaceholder?: string;
  titleRequired?: string;
  descriptionRequired?: string;
};

type ProgramWorkSubmitDialogProps = {
  open: boolean;
  submitting?: boolean;
  success?: boolean;
  submitError?: string;
  text: ProgramWorkSubmitText;
  enableMetadata?: boolean;
  onClose: () => void;
  onSubmit: (workUrl: string, metadata?: { title: string; description: string }) => Promise<void> | void;
  onSubmitError?: (error: unknown) => void;
};

export function ProgramWorkSubmitDialog({
  open,
  submitting = false,
  success = false,
  submitError,
  text,
  enableMetadata = false,
  onClose,
  onSubmit,
  onSubmitError,
}: ProgramWorkSubmitDialogProps) {
  const inputId = useId();
  const [workUrl, setWorkUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setWorkUrl('');
      setTitle('');
      setDescription('');
      setError('');
    }
  }, [open]);

  const handleSubmit = async () => {
    const nextWorkUrl = workUrl.trim();
    if (!isValidProgramWorkUrl(nextWorkUrl)) {
      setError(text.workUrlInvalid);
      return;
    }
    if (enableMetadata && SITE === 'china' && !title.trim()) {
      setError(text.titleRequired ?? text.uploadFailed);
      return;
    }
    if (enableMetadata && SITE === 'china' && !description.trim()) {
      setError(text.descriptionRequired ?? text.uploadFailed);
      return;
    }

    setError('');
    try {
      await onSubmit(
        nextWorkUrl,
        enableMetadata && SITE === 'china' ? { title: title.trim(), description: description.trim() } : undefined,
      );
    } catch (error) {
      if (onSubmitError) {
        onSubmitError(error);
      } else {
        setError(submitError || text.uploadFailed);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="max-w-[480px]" onClose={onClose}>
        <DialogHeader>
          <DialogTitle>{text.uploadTitle}</DialogTitle>
        </DialogHeader>
        {success ? (
          <div className="text-center">
            <CheckCircle2 className="mx-auto size-12 text-system-success" />
            <p className="mt-4 font-medium text-system-text01 text-xl">{text.submitted}</p>
            <Button type="button" variant="primary" size="dialog" onClick={onClose} className="mt-7 w-full">
              {text.confirm}
            </Button>
          </div>
        ) : (
          <div>
            <label htmlFor={inputId} className="block font-medium text-sm text-system-text01">
              {text.workLink}
            </label>
            <Input
              id={inputId}
              type="url"
              value={workUrl}
              onChange={(event) => {
                setWorkUrl(event.target.value);
                setError('');
              }}
              placeholder={text.workUrlPlaceholder}
              aria-invalid={Boolean(error)}
              className="mt-3 h-12 text-system-text01 aria-[invalid=true]:border-system-error1"
            />
            {enableMetadata && SITE === 'china' && (
              <>
                <label htmlFor={`${inputId}-title`} className="mt-4 block font-medium text-sm text-system-text01">
                  {text.title ?? text.workLink}
                </label>
                <Input
                  id={`${inputId}-title`}
                  value={title}
                  maxLength={30}
                  onChange={(event) => {
                    setTitle(event.target.value);
                    setError('');
                  }}
                  placeholder={text.titlePlaceholder ?? text.workUrlPlaceholder}
                  className="mt-3 h-12 text-system-text01 aria-[invalid=true]:border-system-error1"
                />
                <label htmlFor={`${inputId}-description`} className="mt-4 block font-medium text-sm text-system-text01">
                  {text.description ?? text.workLink}
                </label>
                <Textarea
                  id={`${inputId}-description`}
                  value={description}
                  maxLength={150}
                  onChange={(event) => {
                    setDescription(event.target.value);
                    setError('');
                  }}
                  placeholder={text.descriptionPlaceholder ?? text.workUrlPlaceholder}
                  className="mt-3 min-h-24 text-system-text01"
                />
              </>
            )}
            {error && <p className="mt-2 text-sm text-system-error1">{error}</p>}
            <DialogFooter className="mt-7">
              <Button type="button" variant="outline" size="dialog" onClick={onClose} className="sm:flex-1">
                {text.cancel}
              </Button>
              <Button
                type="button"
                variant="primary"
                size="dialog"
                onClick={handleSubmit}
                disabled={submitting}
                loading={submitting}
                className="sm:flex-1"
              >
                {submitting ? text.submitting : text.confirm}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
